/**
 * TrustBar - Floating trust signals bar visible on all public pages
 * Shows key trust metrics: GDPR, rating, 24/7, phone
 */
import { forwardRef } from 'react';
import { Shield, Star, Clock, Phone } from 'lucide-react';

const trustBarContent: Record<string, { gdpr: string; reviews: string; support: string }> = {
  en: { gdpr: 'GDPR Aligned', reviews: '4.9/5 (87 reviews)', support: '24/7 Support' },
  fr: { gdpr: 'Conforme RGPD', reviews: '4.9/5 (87 avis)', support: 'Support 24/7' },
  es: { gdpr: 'GDPR Alineado', reviews: '4.9/5 (87 reseñas)', support: 'Soporte 24/7' },
  ar: { gdpr: 'متوافق مع GDPR', reviews: '4.9/5 (87 تقييم)', support: 'دعم 24/7' },
  de: { gdpr: 'DSGVO-konform', reviews: '4.9/5 (87 Bewertungen)', support: '24/7 Support' },
  it: { gdpr: 'GDPR Conforme', reviews: '4.9/5 (87 recensioni)', support: 'Supporto 24/7' },
  pt: { gdpr: 'RGPD Alinhado', reviews: '4.9/5 (87 avaliações)', support: 'Suporte 24/7' },
  nl: { gdpr: 'AVG Conform', reviews: '4.9/5 (87 beoordelingen)', support: '24/7 Ondersteuning' },
  pl: { gdpr: 'Zgodne z RODO', reviews: '4.9/5 (87 opinii)', support: 'Wsparcie 24/7' },
  ru: { gdpr: 'GDPR Совместимо', reviews: '4.9/5 (87 отзывов)', support: 'Поддержка 24/7' },
  zh: { gdpr: 'GDPR合规', reviews: '4.9/5 (87条评价)', support: '24/7支持' },
  ja: { gdpr: 'GDPR準拠', reviews: '4.9/5 (87件)', support: '24/7サポート' },
  tr: { gdpr: 'GDPR Uyumlu', reviews: '4.9/5 (87 yorum)', support: '7/24 Destek' },
  ko: { gdpr: 'GDPR 준수', reviews: '4.9/5 (87개 리뷰)', support: '24/7 지원' },
  sv: { gdpr: 'GDPR-anpassad', reviews: '4.9/5 (87 recensioner)', support: 'Support dygnet runt' },
  da: { gdpr: 'GDPR-tilpasset', reviews: '4.9/5 (87 anmeldelser)', support: 'Døgnsupport' },
  th: { gdpr: 'สอดคล้อง GDPR', reviews: '4.9/5 (87 รีวิว)', support: 'สนับสนุน 24/7' },
  vi: { gdpr: 'Tuân thủ GDPR', reviews: '4.9/5 (87 đánh giá)', support: 'Hỗ trợ 24/7' },
  el: { gdpr: 'Σύμφωνο GDPR', reviews: '4.9/5 (87 κριτικές)', support: 'Υποστήριξη 24/7' },
  no: { gdpr: 'GDPR-tilpasset', reviews: '4.9/5 (87 anmeldelser)', support: 'Døgnstøtte' },
  hi: { gdpr: 'GDPR अनुरूप', reviews: '4.9/5 (87 समीक्षाएं)', support: '24/7 सहायता' },
  fi: { gdpr: 'GDPR-yhteensopiva', reviews: '4.9/5 (87 arvostelua)', support: '24/7 Tuki' },
  hu: { gdpr: 'GDPR Megfelelő', reviews: '4.9/5 (87 értékelés)', support: '24/7 Támogatás' },
  cs: { gdpr: 'GDPR Soulad', reviews: '4.9/5 (87 hodnocení)', support: '24/7 Podpora' },
  ro: { gdpr: 'Conform GDPR', reviews: '4.9/5 (87 recenzii)', support: 'Suport 24/7' },
  id: { gdpr: 'Sesuai GDPR', reviews: '4.9/5 (87 ulasan)', support: 'Dukungan 24/7' },
  ms: { gdpr: 'Pematuhan GDPR', reviews: '4.9/5 (87 ulasan)', support: 'Sokongan 24/7' },
  bn: { gdpr: 'GDPR সম্মত', reviews: '4.9/5 (87 রিভিউ)', support: '24/7 সহায়তা' },
  tl: { gdpr: 'GDPR Aligned', reviews: '4.9/5 (87 review)', support: '24/7 Suporta' },
};

export const TrustBar = forwardRef<HTMLDivElement, { language?: string }>(function TrustBar({ language = 'en' }, ref) {
  const t = trustBarContent[language] || trustBarContent.en;
  return (
    <div ref={ref} className="hidden md:block fixed bottom-0 left-0 right-0 z-40 bg-slate-brand/95 backdrop-blur-md border-t border-border">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-center gap-8 py-2 text-xs text-primary-foreground/90">
          <span className="flex items-center gap-1.5">
            <Shield className="h-3.5 w-3.5 text-teal" />
            {t.gdpr}
          </span>
          <span className="hidden lg:flex items-center gap-1.5">
            <Star className="h-3.5 w-3.5 text-coral" />
            {t.reviews}
          </span>
          <span className="flex items-center gap-1.5">
            <Clock className="h-3.5 w-3.5 text-teal" />
            {t.support}
          </span>
          <a
            href="tel:+212537373777"
            className="flex items-center gap-1.5 text-primary-foreground/90 hover:text-primary-foreground transition-colors"
          >
            <Phone className="h-3.5 w-3.5 text-coral" />
            +212 5 37 37 37 77
          </a>
        </div>
      </div>
    </div>
  );
});

TrustBar.displayName = 'TrustBar';
